import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
  Flex,
  Badge,
  IconButton,
  Stack,
  Spinner,
  Tag,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox,
  VStack,
  HStack,
  useDisclosure,
  Switch,
  Divider,
} from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { ChevronDownIcon, CloseIcon, ArrowUpIcon } from "@chakra-ui/icons";
import { FilterProto, ObservationColumnProto } from "build-ts/metadata";
import { FilterOperator, ColumnType } from "build-ts/enums";
import ColumnValues from "components/common/ColumnValues";
import { getKGColumnValues } from "services/DataModelService";
import { getFilterDisplayString } from "utils/nameUtils";
import GlobalEntity from "containers/batch/GlobalEntity";
import { DateFilterCategory } from "utils/enum";
import { DateFilter } from "build-ts/relative_date";
import { useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";
import { KGObservationProto } from "build-ts/analysis_plan";


// Define an interface for the combined dropdown items
interface DropdownItem {
  column: ObservationColumnProto;
  type: "attribute" | "temporal";
}

interface AdditionalContextProps {
  currentNodeId: string;
  nonBenchmarkEdges?: ObservationColumnProto[];
  filters: FilterProto[];
  setFilters: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  temporalColumns?: ObservationColumnProto[];
  recommendedFilters?: FilterProto[];
  handlePropogateFilterClick?: (filter: FilterProto, currentNodeId: string) => void;
  availableNodes: Array<{
    id: string;
    name: string;
    selected: boolean;
  }>;
  setAvailableNodes: React.Dispatch<React.SetStateAction<Array<{
    id: string;
    name: string;
    selected: boolean;
  }>>>;
  filterToPropagate: FilterProto | null;
  setFilterToPropagate: React.Dispatch<React.SetStateAction<FilterProto | null>>;
}

const KGAdditionalContext: React.FC<AdditionalContextProps> = ({
  currentNodeId,
  nonBenchmarkEdges,
  filters,
  setFilters,
  temporalColumns,
  recommendedFilters = [], 
  handlePropogateFilterClick,
  availableNodes,
  setAvailableNodes,
  filterToPropagate,
  setFilterToPropagate
}) => {
  // State for selected item and values
  const [selectedItem, setSelectedItem] = useState<DropdownItem | null>(null);
  const [attributeValues, setAttributeValues] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // State for date filter when temporal column is selected
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);
  
  // State for descendant selection modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { nodeMap, updateNode, getKGObservationFromKGStepInterface } = useKGAnalysisPlanContext();

  const [shouldOverwrite, setShouldOverwrite] = useState<boolean>(true);

  // Context for node operations

  // Create combined dropdown items (attribute edges + temporal columns)
  const dropdownItems: DropdownItem[] = [];
 // Add attribute edges
  nonBenchmarkEdges?.filter((column) => 
    column.kg_column_header?.column_type === ColumnType.ATTRIBUTE).forEach((column) => {
    dropdownItems.push({
      column: column,
      type: "attribute"
    });
  });

  // Add temporal columns
  temporalColumns?.forEach((column) => {
    dropdownItems.push({
      column: column,
      type: "temporal"
    });
  });

  // Reset date filter and category when selected item changes
  useEffect(() => {
    if (selectedItem && selectedItem.type === "temporal") {
      setDateFilter(undefined);
      setDateFilterCategory(DateFilterCategory.FIXED);
    }
  }, [selectedItem]);

  // Fetch attribute values when an attribute item is selected
  useEffect(() => {
    if (selectedItem && selectedItem.type === "attribute" && selectedItem.column?.kg_column_header?.id) {
      const fetchValues = async () => {
        setIsLoading(true);
        try {
          const values = await getKGColumnValues(selectedItem.column?.kg_column_header?.id || "");
          setAttributeValues(values || []);
        } catch (error) {
          console.error("Error fetching attribute values:", error);
          setAttributeValues([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchValues();
      // Reset selected values
      setSelectedValues([]);
    }
  }, [selectedItem]);

  // Function to add a filter
  const handleAddFilter = () => {
    if (!selectedItem) return;

    let newFilter: FilterProto | null = null;

    if (selectedItem.type === "attribute") {
      // For attribute type, use multi-select values
      if (selectedValues.length === 0) return;
      
      newFilter = FilterProto.create({
        filter_id: uuidv4(),
        kg_column_header: selectedItem?.column?.kg_column_header,
        operator: FilterOperator.EQ,
        values: selectedValues,
      });
    } else if (selectedItem.type === "temporal") {
      // For temporal type, use the DateFilter
      if (!dateFilter) return;
      
      // Create a filter using the date filter data
      // We'll store the DateFilter as a serialized JSON string in the first value
      newFilter = FilterProto.create({
        filter_id: uuidv4(),
        kg_column_header: selectedItem?.column?.kg_column_header,
        operator: FilterOperator.EQ,
        date_filter: dateFilter,
      });
    }

    if (newFilter) {
      // Add to filters
      setFilters([...filters, newFilter]);

      // Reset state
      setSelectedItem(null);
      setSelectedValues([]);
      setAttributeValues([]);
      setDateFilter(undefined);
    }
  };

  // Function to handle node selection toggle
  const handleNodeToggle = (nodeId: string) => {
    setAvailableNodes(prev => 
      prev.map(node => 
        node.id === nodeId 
          ? { ...node, selected: !node.selected }
          : node
      )
    );
  };

  // Function to confirm propagation to selected nodes
  const handleConfirmPropagation = () => {
    if (!filterToPropagate) return;

    // Only use selected nodes for propagation
    const selectedNodeIds = availableNodes
      .filter(node => node.selected)
      .map(node => node.id);

    selectedNodeIds.forEach(nodeId => {
      const targetNode = nodeMap?.get(nodeId);
      if (targetNode) {
        if (shouldOverwrite) {
          const existingFilterIndex = targetNode.selected_filters?.findIndex(
            existingFilter => existingFilter.kg_column_header?.id === filterToPropagate.kg_column_header?.id
          );
          if (existingFilterIndex !== undefined && existingFilterIndex >= 0) {
            targetNode.selected_filters?.splice(existingFilterIndex, 1);
          }
        }
        // Create a updated KG observation for this node.
        const kgObservation = KGObservationProto.create({
          ...getKGObservationFromKGStepInterface(nodeId),
          filters: [...(targetNode.selected_filters || []), filterToPropagate],
        });
        targetNode.kg_observation = kgObservation;
        targetNode.selected_filters?.push(filterToPropagate);
        updateNode(targetNode);
      }
    });

    // Close modal and reset state
    onClose();
    setFilterToPropagate(null);
    setAvailableNodes([]);
    setShouldOverwrite(true); // Reset to default
  };

  // Function to remove a filter
  const handleRemoveFilter = (filterId: string) => {
    setFilters(filters.filter((filter) => filter.kg_column_header?.id !== filterId));
  };

  // Helper function to get item display name
  const getItemDisplayName = (item: DropdownItem | null): string => {
    return item?.column?.kg_column_header?.name || "Unnamed";
  };
  
  // Check if a filter column is in the recommended filters
  const isRecommendedFilter = (columnId: string): boolean => {
    return recommendedFilters.some(filter => 
      filter.kg_column_header?.id === columnId
    );
  };
  
  return (
    <Stack spacing={4}>
      {/* Recommended Filters Section */}
      {recommendedFilters?.length > 0 && (
        <Box mb={2} borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
          <Text fontWeight="medium" fontSize="sm" mb={1}>
            Filters from Parent Recommendations:
          </Text>
          <Stack spacing={1}>
            {recommendedFilters?.map((filter, index) => (
              <Flex
                key={index}
                borderWidth="1px"
                borderRadius="md"
                p={1}
                alignItems="center"
                backgroundColor="white"
              >
                <Box overflow="hidden" textOverflow="ellipsis">
                  <Text fontSize="sm" mb={0} fontWeight="medium">
                    {filter?.kg_column_header?.name || "Unknown Column"}
                      {` = [${getFilterDisplayString(filter)}]`}
                  </Text>
                </Box>
                <Tag size="sm" colorScheme="orange" ml="auto">
                  Added from parent
                </Tag>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}
      
      {/* Column Selection Dropdown */}
      <FormControl>
        <FormLabel>Select Column</FormLabel>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="blue"
            isDisabled={dropdownItems.length === 0}
          >
            {selectedItem
              ? getItemDisplayName(selectedItem)
              : dropdownItems.length === 0
              ? "No columns available"
              : "Select a column"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {dropdownItems.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => setSelectedItem(item)}
                  _hover={{ bg: "blue.50" }}
                  isDisabled={isRecommendedFilter(item.column.kg_column_header?.id || "")}
                >
                  <Flex alignItems="center" width="100%">
                    {getItemDisplayName(item)}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Values Selection - only visible when an item is selected */}
      {selectedItem && (
        <FormControl>
          <FormLabel>
            {selectedItem.type === "attribute"
              ? "Select Values"
              : "Configure Date Filter"}
          </FormLabel>

          {selectedItem.type === "attribute" ? (
            // For attribute columns - show multi-select
            isLoading ? (
              <Flex justify="center" align="center" py={4}>
                <Spinner size="sm" mr={2} />
                <Text>Loading values...</Text>
              </Flex>
            ) : (
              <ColumnValues
                contextValues={attributeValues}
                selectedOptions={selectedValues}
                setSelectedOptions={setSelectedValues}
              />
            )
          ) : (
            // For temporal columns - show GlobalEntity date filter component
            <Box>
              <GlobalEntity
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                dateFilterCategory={dateFilterCategory}
                setDateFilterCategory={setDateFilterCategory}
              />
            </Box>
          )}
        </FormControl>
      )}

      {/* Add Filter Button - only visible when an item is selected */}
      {selectedItem && (
        <Button
          colorScheme="blue"
          onClick={handleAddFilter}
          isDisabled={
            (selectedItem.type === "attribute" && selectedValues.length === 0) ||
            (selectedItem.type === "temporal" && !dateFilter)
          }
          width="100%"
        >
          Add Filter
        </Button>
      )}

      {/* Display added filters */}
      {filters.length > 0 && (
        <Box mt={0}>
          <Text fontWeight="medium" mb={2}>
            Applied Filters:
          </Text>
          <Stack spacing={2}>
            {filters?.map((filter) => (
              <Flex
                key={filter.kg_column_header?.id}
                borderWidth="1px"
                borderRadius="md"
                p={1}
                justifyContent="space-between"
                alignItems="center"
                backgroundColor="gray.50"
              >
                <Flex alignItems="center" gap={2} flexWrap="wrap">
                  <Text fontWeight="bold" fontSize="sm" mb={0}>
                    {filter.kg_column_header?.name || "Unnamed"}:
                  </Text>
                  <Badge colorScheme="blue" fontSize="xs" textTransform="none">
                    {getFilterDisplayString(filter)}
                  </Badge>
                  {/* Show badge for recommended filter */}
                  {isRecommendedFilter(filter.kg_column_header?.id || "") && (
                    <Badge colorScheme="orange" fontSize="xs">
                      From recommendation
                    </Badge>
                  )}
                </Flex>
                {/* Group action icons together */}
                <Flex alignItems="center" gap={2}>
                  <IconButton
                    aria-label="Propogate filter"
                    icon={<ArrowUpIcon color="green.500" strokeWidth="2.5" />}
                    onClick={() => 
                      {
                        onOpen();
                        handlePropogateFilterClick?.(filter, currentNodeId);
                      }
                    }
                    size="xs"
                    variant="ghost"
                    colorScheme="green"
                  />
                  <IconButton
                    aria-label="Remove filter"
                    icon={<CloseIcon />}
                    onClick={() => handleRemoveFilter(filter.kg_column_header?.id || "")}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                  />
                </Flex>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}

      {/* Node Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Select Nodes for Filter Propagation
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Filter to propagate:
                </Text>
                <Badge colorScheme="blue" fontSize="sm">
                  {filterToPropagate?.kg_column_header?.name}: {getFilterDisplayString(filterToPropagate!)}
                </Badge>
              </Box>
              
              <Divider />
              
              <Box>
                <HStack justify="space-between" alignItems="center">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      Overwrite existing filters
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {shouldOverwrite 
                        ? "Replace existing filters of the same column type" 
                        : "Add new filter alongside existing ones"}
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={shouldOverwrite}
                    onChange={(e) => setShouldOverwrite(e.target.checked)}
                    colorScheme="blue"
                    size="md"
                  />
                </HStack>
              </Box>
              
              <Divider />
              
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">
                    Select target nodes ({availableNodes?.filter(node => node.selected).length} of {availableNodes?.length}):
                  </Text>
                  <HStack spacing={2}>
                  </HStack>
                </HStack>
                
                <VStack spacing={1} maxH="300px" overflowY="auto" align="stretch">
                  {/* Select All option */}
                  <Flex
                    p={2}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={
                      availableNodes?.every(node => node.selected) && availableNodes?.length > 0
                        ? "blue.200"
                        : "gray.200"
                    }
                    bg={
                      availableNodes?.every(node => node.selected) && availableNodes?.length > 0
                        ? "blue.50"
                        : "white"
                    }
                    align="center"
                    gap={2}
                  >
                    <Checkbox
                      isChecked={availableNodes?.length > 0 && availableNodes?.every(node => node.selected)}
                      isIndeterminate={
                        availableNodes?.some(node => node.selected) &&
                        !availableNodes?.every(node => node.selected)
                      }
                      onChange={e => {
                        const checked = e.target.checked;
                        setAvailableNodes(prev =>
                          prev.map(node => ({ ...node, selected: checked }))
                        );
                      }}
                      colorScheme="blue"
                      m={0}
                      p={0}
                    />
                    <Text fontSize="sm" fontWeight="bold" m={0} p={0}>Select All</Text>
                  </Flex>
                  {/* Individual nodes options */}
                  {availableNodes?.map((node) => (
                    <Flex
                      key={node.id}
                      p={2}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={node?.selected ? "blue.200" : "gray.200"}
                      bg={node?.selected ? "blue.50" : "white"}
                      align="center"
                      gap={2}
                    >
                      <Checkbox
                        isChecked={node?.selected}
                        onChange={() => handleNodeToggle(node.id)}
                        colorScheme="blue"
                        m={0}
                        p={0}
                      />
                      <Text fontSize="sm" m={0} p={0}>{node?.name}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleConfirmPropagation}
              isDisabled={availableNodes?.filter(node => node.selected).length === 0}
            >
              Propagate to Selected ({availableNodes?.filter(node => node.selected).length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default KGAdditionalContext;
